import { forwardRef } from 'react';
import { type Icon as LucideIcon, Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Icon } from './Icon';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  icon?: LucideIcon;
  iconClassName?: string;
  isLoading?: boolean;
  fullWidth?: boolean;
  as?: React.ElementType;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', icon: Icon, iconClassName, isLoading, fullWidth, as, children, ...props }, ref) => {
    const Comp = as || 'button';
    
    return (
      <Comp
        className={cn(
          'inline-flex items-center justify-center font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2',
          {
            'px-2.5 py-1.5 text-sm': size === 'sm',
            'px-4 py-2 text-sm': size === 'md',
            'px-6 py-3 text-base': size === 'lg',
            'w-full': fullWidth,
            'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500': variant === 'primary',
            'bg-white text-gray-700 hover:bg-gray-50 focus:ring-primary-500': variant === 'secondary',
            'border-2 border-primary-500 text-primary-600 hover:bg-primary-50 focus:ring-primary-500': variant === 'outline',
          },
          className
        )}
        ref={ref}
        {...props}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            {Icon && <Icon className={cn("w-5 h-5 mr-2", iconClassName)} />}
            {children}
          </>
        )}
      </Comp>
    );
  }
);
